import { useState, useEffect } from 'react';
import { getAllCountries, saveStream } from '../../../services/firestoreService';
import { useAuth } from '../../../hooks/useAuth';
import FormField from '../../shared/Forms/FormField';
import SelectField from '../../shared/Forms/SelectField';
import ToggleField from '../../shared/Forms/ToggleField';
import MultiSelect from '../../shared/Forms/MultiSelect';
import Button from '../../shared/UI/Button';

export default function StreamForm({ stream, fundId, fundName, onSaved, onCancel }) {
  const { user } = useAuth();
  const [form, setForm] = useState(stream ? { ...stream } : {
    name: '', short_name: '', description: '',
    territories: [], base_currency: '', legal_framework: '',
    has_custom_rubric: false, has_custom_ddq: false,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    getAllCountries().then(list => setCountries(list.filter(c => c.is_active !== false)));
  }, []);

  const currencyOptions = countries.map(c => ({
    value: c.currency_code,
    label: `${c.currency_code} — ${c.currency_name}`,
  }));
  const countryOptions = countries.map(c => ({
    value: c.id,
    label: `${c.name} (${c.id})`,
  }));

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  }

  function validate() {
    const e = {};
    if (!form.name?.trim()) e.name = 'Stream name is required';
    if (!form.short_name?.trim()) e.short_name = 'Short name is required';
    if (form.short_name?.length > 10) e.short_name = 'Must be 10 characters or fewer';
    if (!form.description?.trim()) e.description = 'Description is required';
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      await saveStream({ fundId, streamData: form, performedBy: user.uid });
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onSaved(); }, 1800);
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {errors._global && <div className="alert alert-error">{errors._global}</div>}
      {success && <div className="alert alert-success">Stream saved successfully!</div>}

      <div className="form-grid mb-4">
        <FormField label="Fund" hint="Read-only">
          <input className="form-input" value={fundName || fundId} readOnly
            style={{ background: 'var(--color-border-light)', cursor: 'not-allowed' }} />
        </FormField>
        <FormField label="Stream Name" required error={errors.name}>
          <input className={`form-input ${errors.name ? 'error' : ''}`}
            value={form.name} onChange={e => set('name', e.target.value)} />
        </FormField>
        <FormField label="Short Name" required error={errors.short_name} hint="Max 10 characters">
          <input className={`form-input ${errors.short_name ? 'error' : ''}`}
            value={form.short_name} maxLength={10}
            onChange={e => set('short_name', e.target.value)} />
        </FormField>
        <SelectField label="Legal Framework" value={form.legal_framework}
          onChange={e => set('legal_framework', e.target.value)}
          options={[
            { value: 'common_law', label: 'Common Law' },
            { value: 'civil_law', label: 'Civil Law' },
            { value: 'mixed', label: 'Mixed' },
          ]} />
        <SelectField label="Base Currency" value={form.base_currency}
          onChange={e => set('base_currency', e.target.value)}
          options={currencyOptions} placeholder="Select currency…" />
        <FormField label="Description" required error={errors.description} className="form-full">
          <textarea className={`form-textarea ${errors.description ? 'error' : ''}`}
            value={form.description} onChange={e => set('description', e.target.value)} rows={3} />
        </FormField>
        <div className="form-full">
          <MultiSelect label="Territories (Countries)" options={countryOptions}
            selected={form.territories || []}
            onChange={v => set('territories', v)} />
        </div>
        <ToggleField label="Has Custom Rubric" checked={form.has_custom_rubric}
          onChange={v => set('has_custom_rubric', v)} />
        <ToggleField label="Has Custom DDQ" checked={form.has_custom_ddq}
          onChange={v => set('has_custom_ddq', v)} />
      </div>

      <div className="flex-between">
        <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit" loading={saving}>
          {stream ? 'Save Changes' : 'Create Stream'}
        </Button>
      </div>
    </form>
  );
}
